"use client";

/**
 * EmailDraftDisplay - Email draft preview and editing
 */

import { useState } from "react";
import { Card, Tag, Tooltip } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import {
  Email,
  ContentCopy,
  AttachFile,
  Code,
  Visibility,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolDivider,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface EmailDraft {
  to?: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject?: string;
  body?: string;
  htmlBody?: string;
  attachments?: Array<{
    name: string;
    size?: number;
    type?: string;
  }>;
  template?: string;
  variables?: Record<string, string>;
}

interface DraftOutput {
  success?: boolean;
  draft?: EmailDraft;
  draftId?: string;
  message?: string;
  error?: string;
}

export function EmailDraftDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showHtml, setShowHtml] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as DraftOutput | undefined;
  const draft = result?.draft;

  const handleCopyBody = async () => {
    if (draft?.body) {
      await navigator.clipboard.writeText(draft.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatRecipients = (recipients: string | string[] | undefined): string => {
    if (!recipients) return "";
    if (Array.isArray(recipients)) return recipients.join(", ");
    return recipients;
  };

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title="Email Draft"
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Email style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Generating draft..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && draft && (
          <div>
            {/* Email Header */}
            <div style={{ padding: spacing[5], backgroundColor: primitives.neutral[50] }}>
              <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
                {/* To */}
                <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                  <span style={{ ...textStyles.body2, width: 60, fontWeight: 500, color: colors.text.muted }}>
                    To:
                  </span>
                  <span style={{ ...textStyles.body2 }}>
                    {formatRecipients(draft.to) || "\u2014"}
                  </span>
                </div>

                {/* CC */}
                {draft.cc && (
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <span style={{ ...textStyles.body2, width: 60, fontWeight: 500, color: colors.text.muted }}>
                      CC:
                    </span>
                    <span style={{ ...textStyles.body2 }}>
                      {formatRecipients(draft.cc)}
                    </span>
                  </div>
                )}

                {/* BCC */}
                {draft.bcc && (
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <span style={{ ...textStyles.body2, width: 60, fontWeight: 500, color: colors.text.muted }}>
                      BCC:
                    </span>
                    <span style={{ ...textStyles.body2 }}>
                      {formatRecipients(draft.bcc)}
                    </span>
                  </div>
                )}

                {/* Subject */}
                <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                  <span style={{ ...textStyles.body2, width: 60, fontWeight: 500, color: colors.text.muted }}>
                    Subject:
                  </span>
                  <span style={{ ...textStyles.body2, fontWeight: 500 }}>
                    {draft.subject || "\u2014"}
                  </span>
                </div>

                {/* Template */}
                {draft.template && (
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <span style={{ ...textStyles.body2, width: 60, fontWeight: 500, color: colors.text.muted }}>
                      Template:
                    </span>
                    <Tag size="sm" variant="default" color="neutral">
                      {draft.template}
                    </Tag>
                  </div>
                )}
              </div>
            </div>

            <ToolDivider />

            {/* Email Body */}
            <div style={{ padding: spacing[5] }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing[4] }}>
                <span style={{ ...textStyles.body2, fontWeight: 600 }}>
                  Message Body
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                  {/* HTML/Plain toggle */}
                  {draft.htmlBody && (
                    <div
                      style={{
                        display: "flex",
                        border: `1px solid ${colors.border.subtle}`,
                        borderRadius: radius.sm,
                        overflow: "hidden",
                      }}
                    >
                      <button
                        onClick={() => setShowHtml(true)}
                        style={{
                          border: "none",
                          padding: `${spacing[1]}px ${spacing[2]}px`,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          backgroundColor: showHtml ? colors.action.productLight : "transparent",
                          color: showHtml ? colors.action.product : colors.text.muted,
                          ...textStyles.caption,
                          fontWeight: showHtml ? 600 : 400,
                        }}
                      >
                        <Visibility style={{ fontSize: 14 }} />
                        Preview
                      </button>
                      <button
                        onClick={() => setShowHtml(false)}
                        style={{
                          border: "none",
                          borderLeft: `1px solid ${colors.border.subtle}`,
                          padding: `${spacing[1]}px ${spacing[2]}px`,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          backgroundColor: !showHtml ? colors.action.productLight : "transparent",
                          color: !showHtml ? colors.action.product : colors.text.muted,
                          ...textStyles.caption,
                          fontWeight: !showHtml ? 600 : 400,
                        }}
                      >
                        <Code style={{ fontSize: 14 }} />
                        Plain
                      </button>
                    </div>
                  )}
                  <Tooltip content={copied ? "Copied!" : "Copy body"}>
                    <button
                      onClick={handleCopyBody}
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        padding: spacing[1],
                        borderRadius: radius.sm,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: colors.text.muted,
                      }}
                    >
                      <ContentCopy style={{ fontSize: 20 }} />
                    </button>
                  </Tooltip>
                </div>
              </div>
              <Card
                variant="outlined"
                padding="sm"
                style={{
                  borderRadius: radius.md,
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                {showHtml && draft.htmlBody ? (
                  <div
                    style={{ ...textStyles.body2 }}
                    dangerouslySetInnerHTML={{ __html: draft.htmlBody }}
                  />
                ) : (
                  <span
                    style={{
                      ...textStyles.body2,
                      whiteSpace: "pre-wrap",
                      fontFamily: "inherit",
                    }}
                  >
                    {draft.body || "No body content"}
                  </span>
                )}
              </Card>
            </div>

            {/* Variables */}
            {draft.variables && Object.keys(draft.variables).length > 0 && (
              <>
                <ToolDivider />
                <div style={{ padding: spacing[5] }}>
                  <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[4], display: "block" }}>
                    Template Variables
                  </span>
                  <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                    {Object.entries(draft.variables).map(([key, value]) => (
                      <Tag key={key} size="sm" variant="default" color="neutral">
                        {`${key}: ${value}`}
                      </Tag>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Attachments */}
            {draft.attachments && draft.attachments.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ padding: spacing[5] }}>
                  <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[4], display: "block" }}>
                    Attachments ({draft.attachments.length})
                  </span>
                  <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                    {draft.attachments.map((attachment, i) => (
                      <Tag key={i} size="sm" variant="default" color="neutral" icon={<AttachFile style={{ fontSize: 14 }} />}>
                        {attachment.name}
                      </Tag>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Draft ID */}
            {result?.draftId && (
              <>
                <ToolDivider />
                <div style={{ padding: `${spacing[4]}px ${spacing[5]}px`, backgroundColor: primitives.neutral[50] }}>
                  <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                    Draft ID: {result.draftId}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {!isLoading && !draft && !isError && result?.message && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <span style={{ ...textStyles.body2, color: colors.text.muted }}>
              {result.message}
            </span>
          </div>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default EmailDraftDisplay;
